import type { CallbackInterface } from "recoil";

import {
	deleteRecipient,
	editFilter,
	fetchModalDeletePreferences,
	getRecipientSubscriptionLists,
	notifyDeletion,
	notifyUnsubscription,
	saveModalDeletePreferences,
	unsubscribeRecipient,
	unsubscribeRecipientMultilist,
} from "Pages/RecipientsList/recoil/services/modals";

import { reorder } from "Pages/RecipientsList/recoil/services/columns";
import { updatePagePreferences } from "Pages/RecipientsList/recoil/services/filters";
import { panelEditRecipientState } from "Pages/RecipientsList/recoil/state/recipient";
import {
	paginationState,
	selectionState,
} from "Pages/RecipientsList/recoil/state/results";

import {
	modalColumnsState,
	modalDeleteState,
	modalFilterDetailsState,
	modalUnsubscribeState,
} from "Pages/RecipientsList/recoil/state/modals";

import { columnsState } from "Pages/RecipientsList/recoil/state/columns";

import { fetchResultsCallback } from "Pages/RecipientsList/recoil/callbacks/callbacks";
import { panelEditRecipientOpenCallback } from "Pages/RecipientsList/recoil/callbacks/recipient";

import type { PageStateType } from "Types/state";

import { pageState } from "State/page";

import { logAppError } from "Helpers/logger";

import getIntl from "Helpers/intl";

import type {
	ColumnName,
	ModalFilterDetailsState,
} from "Pages/RecipientsList/model";
import {
	fetchLists,
	getCurrentList,
} from "Pages/RecipientsList/recoil/services/common";
import { listsState } from "Pages/RecipientsList/recoil/state/common";
import { gainsightTrack } from "mailup-react-helpers";

export const modalUnsubscribeOpenCallback =
	({ set, snapshot }: CallbackInterface) =>
	async (idRecipient: number) => {
		const { loaded } = await snapshot.getPromise(listsState);
		const idList = getCurrentList();

		try {
			if (!loaded) {
				const lists = await fetchLists();

				set(listsState, (prev) => ({
					...prev,
					items: lists,
					loaded: true,
				}));
			}

			const recipientSubscriptionLists =
				await getRecipientSubscriptionLists(idRecipient);

			set(modalUnsubscribeState, (prev) => ({
				...prev,
				idRecipient,
				show: true,
				lists: [idList],
				allLists: false,
				recipientSubscriptionLists,
			}));
		} catch (error) {
			console.error("### error", error);

			logAppError({
				Component: "RecipientsList",
				Method: "modalUnsubscribeOpenCallback",
				appError: error,
			});

			set(modalUnsubscribeState, (prev) => ({
				...prev,
				show: false,
				idRecipient: undefined,
				wipeData: false,
			}));

			set<PageStateType>(pageState, (prev) => ({
				...prev,
				status: "error",
				loading: false,
				errorMessage: getIntl().formatMessage({
					id: "Load.Alerts.ErrorGeneric",
				}),
			}));
		}
	};

export const modalUnsubscribeToggleFieldCallback =
	({ set }: CallbackInterface) =>
	async (field: "channelEmail" | "channelSMS" | "wipeData" | "allLists") => {
		set(modalUnsubscribeState, (prev) => ({
			...prev,
			[field]: !prev[field],
		}));
	};

export const modalUnsubscribeChangeListsCallback =
	({ set }: CallbackInterface) =>
	async (value: number[]) => {
		set(modalUnsubscribeState, (prev) => ({
			...prev,
			lists: value,
		}));
	};

export const modalUnsubscribeCancelCallback =
	({ set, reset }: CallbackInterface) =>
	async () => {
		set(modalUnsubscribeState, (prev) => ({
			...prev,
			show: false,
			idRecipient: undefined,
			wipeData: false,
		}));
	};

export const modalUnsubscribeConfirmCallback =
	({ set, snapshot, reset, ...cbi }: CallbackInterface) =>
	async () => {
		gainsightTrack("mup_recipients_click_modalUnsubscribeButton", {});
		const { idRecipient, channelEmail, channelSMS, wipeData, lists, allLists } =
			await snapshot.getPromise(modalUnsubscribeState);

		const { items } = await snapshot.getPromise(listsState);

		const { show } = await snapshot.getPromise(panelEditRecipientState);

		if (idRecipient !== undefined) {
			set(pageState, (prev) => ({
				...prev,
				loading: true,
			}));

			try {
				await unsubscribeRecipientMultilist(
					idRecipient,
					channelEmail,
					channelSMS,
					wipeData,
					lists,
					allLists,
					items,
				);

				await updatePagePreferences({
					modalUnsubscribe: {
						channelEmail,
						channelSMS,
					},
				});

				set(modalUnsubscribeState, (prev) => ({
					...prev,
					show: false,
					idRecipient: undefined,
					wipeData: false,
				}));

				set(pageState, (prev) => ({
					...prev,
					loading: false,
				}));

				notifyUnsubscription(lists, allLists);

				if (show) {
					await panelEditRecipientOpenCallback({
						set,
						snapshot,
						reset,
						...cbi,
					})(idRecipient);
				}

				await fetchResultsCallback({ set, snapshot, reset, ...cbi })({});
			} catch (error) {
				console.error("### error", error);

				logAppError({
					Component: "RecipientsList",
					Method: "modalUnsubscribeConfirmCallback",
					appError: error,
				});

				set(modalUnsubscribeState, (prev) => ({
					...prev,
					show: false,
					idRecipient: undefined,
					wipeData: false,
				}));

				set<PageStateType>(pageState, (prev) => ({
					...prev,
					status: "error",
					loading: false,
					errorMessage: getIntl().formatMessage({
						id: "Load.Alerts.ErrorGeneric",
					}),
				}));
			}
		}
	};

//

export const modalDeleteOpenCallback =
	({ set }: CallbackInterface) =>
	async (idRecipient: number) => {
		const prefs = fetchModalDeletePreferences();

		set(modalDeleteState, (prev) => ({
			...prev,
			...prefs,
			idRecipient,
			show: true,
		}));
	};

export const modalDeleteCancelCallback =
	({ reset }: CallbackInterface) =>
	async () => {
		reset(modalDeleteState);
	};

export const modalDeleteChangeCallback =
	({ set }: CallbackInterface) =>
	async (mode: "delete" | "unsubscribe") => {
		set(modalDeleteState, (prev) => ({
			...prev,
			mode,
		}));
	};

export const modalDeleteConfirmCallback =
	({ set, snapshot, reset, ...cbi }: CallbackInterface) =>
	async () => {
		const { idRecipient, mode } = await snapshot.getPromise(modalDeleteState);

		const { show } = await snapshot.getPromise(panelEditRecipientState);

		if (idRecipient !== undefined) {
			set(pageState, (prev) => ({
				...prev,
				loading: true,
			}));

			try {
				await (mode === "delete"
					? deleteRecipient(idRecipient)
					: unsubscribeRecipient(idRecipient, true, true, false));

				saveModalDeletePreferences({ mode });

				reset(modalDeleteState);

				set(pageState, (prev) => ({
					...prev,
					loading: false,
				}));

				//
				if (mode === "delete") {
					set(selectionState, ({ checked, ...prev }) => ({
						...prev,
						checked: checked.filter((id) => id !== idRecipient),
					}));
				}

				mode === "delete" ? notifyDeletion() : notifyUnsubscription([], false);

				if (show && mode === "unsubscribe") {
					await panelEditRecipientOpenCallback({
						set,
						snapshot,
						reset,
						...cbi,
					})(idRecipient);
				}

				if (show && mode === "delete") {
					reset(panelEditRecipientState);
				}

				await fetchResultsCallback({ set, snapshot, reset, ...cbi })({});
			} catch (error) {
				console.error("### error", error);

				logAppError({
					Component: "RecipientsList",
					Method: "modalDeleteConfirmCallback",
					appError: error,
				});

				reset(modalDeleteState);

				set<PageStateType>(pageState, (prev) => ({
					...prev,
					status: "error",
					loading: false,
					errorMessage: getIntl().formatMessage({
						id: "Load.Alerts.ErrorGeneric",
					}),
				}));
			}
		}
	};

//

export const modalConfigColumnsOpenCallback =
	({ set, snapshot }: CallbackInterface) =>
	async () => {
		const columns = await snapshot.getPromise(columnsState);

		set(modalColumnsState, (prev) => ({
			...prev,
			items: [...columns],
			show: true,
		}));
	};

export const modalConfigColumnsCancelCallback =
	({ set, snapshot, reset }: CallbackInterface) =>
	async () => {
		reset(modalColumnsState);
	};

export const modalConfigColumnsAddColumnCallback =
	({ set }: CallbackInterface) =>
	async () => {
		set(modalColumnsState, ({ items, ...prev }) => ({
			...prev,
			items: [...items, undefined],
		}));
	};

export const modalConfigColumnsRemoveColumnCallback =
	({ set }: CallbackInterface) =>
	async (index: number) => {
		set(modalColumnsState, ({ items, ...prev }) => ({
			...prev,
			items: items.filter((_, idx) => idx !== index),
		}));
	};

export const modalConfigColumnsChangeColumnCallback =
	({ set }: CallbackInterface) =>
	async (index: number, value: ColumnName) => {
		set(modalColumnsState, ({ items, ...prev }) => ({
			...prev,
			items: items.map((v, idx) => (idx === index ? value : v)),
		}));
	};

export const modalConfigColumnsReorderCallback =
	({ set }: CallbackInterface) =>
	async (from: number, to: number) => {
		//

		set(modalColumnsState, ({ items, ...prev }) => ({
			...prev,
			items: reorder(items, from, to),
		}));
	};

export const modalConfigColumnsConfirmCallback =
	({ set, snapshot, reset, ...cbi }: CallbackInterface) =>
	async () => {
		const { items } = await snapshot.getPromise(modalColumnsState);
		const pagination = await snapshot.getPromise(paginationState);

		const newColumns = items.filter((c) => c !== undefined) as ColumnName[];

		set(columnsState, newColumns);

		reset(modalColumnsState);

		const orderByColumnInVisibleColumns = newColumns.includes(
			pagination.orderBy,
		);

		await fetchResultsCallback({ set, snapshot, reset, ...cbi })({
			columns: newColumns,
			orderBy: orderByColumnInVisibleColumns ? undefined : "Id",
			orderDescending: orderByColumnInVisibleColumns ? undefined : true,
		});
	};

//

export const modalFilterDetailsOpen =
	({ set }: CallbackInterface) =>
	async ({
		id,
		name,
		description,
		type,
	}: Pick<ModalFilterDetailsState, "id" | "name" | "description" | "type">) => {
		set<ModalFilterDetailsState>(modalFilterDetailsState, {
			show: true,
			id,
			name,
			description,
			type,
		});
	};

export const modalFilterDetailsCancel =
	({ reset }: CallbackInterface) =>
	async () => {
		reset(modalFilterDetailsState);
	};

export const modalFilterDetailsEdit =
	({ reset, snapshot }: CallbackInterface) =>
	async () => {
		const { id } = await snapshot.getPromise(modalFilterDetailsState);

		editFilter({ id });

		reset(modalFilterDetailsState);
	};
